import type { BillingTier } from './billing';

/** Mirrors the API's TRIAL_DAYS: every new account's first workspace gets this many days of Pro. */
export const TRIAL_DAYS = 14;

/** From this many days left, trial reminders turn amber and name what pauses. */
export const TRIAL_URGENT_DAYS = 3;

export interface WorkspaceTrial {
  tier: BillingTier;
  endsAt: string;
  active: boolean;
  daysLeft: number;
}

export function daysLeftLabel(daysLeft: number): string {
  if (daysLeft <= 0) return 'ends today';
  return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
}

export function isTrialUrgent(trial: WorkspaceTrial | null | undefined): boolean {
  return !!trial?.active && trial.daysLeft <= TRIAL_URGENT_DAYS;
}

/** What the workspace actually uses that stops working when the trial ends on Starter. */
export interface TrialPauseContext {
  publicKeyExists?: boolean;
  brandingCustomised?: boolean;
  webhookCount?: number;
  embedOriginsConfigured?: boolean;
}

/**
 * "Your SDK key and branding pause on Starter." Names only what this workspace relies on, falling
 * back to the headline Pro features so the sentence is never empty.
 */
export function trialPauseSentence(context: TrialPauseContext): string {
  const items: string[] = [];
  if (context.publicKeyExists) items.push('your SDK key');
  if (context.brandingCustomised) items.push('your white-label branding');
  if (context.webhookCount && context.webhookCount > 0) {
    items.push(context.webhookCount === 1 ? 'your webhook' : `your ${context.webhookCount} webhooks`);
  }
  if (context.embedOriginsConfigured) items.push('your product embeds');
  if (items.length === 0) return 'White-label branding, SDK keys and webhooks pause on Starter.';
  const list = items.length === 1
    ? items[0]!
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  const sentence = `${list.charAt(0).toUpperCase()}${list.slice(1)}`;
  return `${sentence} ${items.length === 1 && !list.endsWith('webhooks') ? 'pauses' : 'pause'} on Starter.`;
}
